import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, Typography, Paper, Grid, Alert,
  FormControlLabel, Switch, Divider,
} from '@mui/material';
import api from '../../api/client';

export default function SecurityPage() {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/system-config').then((r) => setConfig(r.data)).catch(() => setError('Failed to load'));
  }, []);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((c) => ({ ...c, [field]: e.target.type === 'number' ? Number(e.target.value) : e.target.value }));
  };

  const handleToggle = (field: string) => (_: any, checked: boolean) => {
    setConfig((c) => ({ ...c, [field]: checked }));
  };

  const handleSave = async () => {
    setSaved(false);
    setError('');
    try {
      const res = await api.patch('/system-config', {
        password_min_length: config.password_min_length,
        password_require_uppercase: config.password_require_uppercase,
        password_require_lowercase: config.password_require_lowercase,
        password_require_digit: config.password_require_digit,
        password_require_special: config.password_require_special,
        password_expiry_days: config.password_expiry_days,
        require_totp_dispatch: config.require_totp_dispatch,
        require_totp_mdt: config.require_totp_mdt,
      });
      setConfig(res.data);
      setSaved(true);
    } catch {
      setError('Failed to save');
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>Password & Security Policy</Typography>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Policy saved</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Password Requirements</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Minimum Password Length" type="number" value={config.password_min_length ?? 8} onChange={handleChange('password_min_length')} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Password Expiry (days)" type="number" value={config.password_expiry_days ?? 90} onChange={handleChange('password_expiry_days')} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel control={<Switch checked={config.password_require_uppercase ?? true} onChange={handleToggle('password_require_uppercase')} />} label="Require uppercase letter" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel control={<Switch checked={config.password_require_lowercase ?? true} onChange={handleToggle('password_require_lowercase')} />} label="Require lowercase letter" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel control={<Switch checked={config.password_require_digit ?? true} onChange={handleToggle('password_require_digit')} />} label="Require digit" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel control={<Switch checked={config.password_require_special ?? false} onChange={handleToggle('password_require_special')} />} label="Require special character" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Two-Factor Authentication (TOTP)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When enabled, users will be required to set up and use an authenticator app (Google Authenticator, Microsoft Authenticator, etc.) on login.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={<Switch checked={config.require_totp_dispatch ?? false} onChange={handleToggle('require_totp_dispatch')} />}
              label="Require TOTP for Dispatch users"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={<Switch checked={config.require_totp_mdt ?? false} onChange={handleToggle('require_totp_mdt')} />}
              label="Require TOTP for MDT users"
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Individual users can also have TOTP required via their user settings, regardless of this policy.
          Admins can reset a user's TOTP from the Users or Personnel page if they get a new phone.
        </Typography>

        <Button variant="contained" sx={{ mt: 3 }} onClick={handleSave}>Save Security Policy</Button>
      </Paper>
    </Box>
  );
}
